import type { IDriveItem, GraphService, IFolderProtection } from './GraphService';
import type { IFileItem } from '../models/FileItem';
import type { IFolderItem } from '../models/FolderItem';
import type { ILibraryInfo } from '../models/LibraryInfo';
import type { IScanResult } from '../models/ScanResult';

export interface IScanProgress {
  itemsRead: number;
}

/**
 * Top-level folders the organizer manages itself. Their contents are counted but not
 * analyzed, so quarantined items aren't flagged (and quarantined) again.
 */
export const MANAGED_FOLDER_NAMES: string[] = ['_quarantine'];

/** Top-level folder where people drop new files for the organizer to suggest a home for. */
export const INTAKE_FOLDER_NAME: string = '_Intake';

function getExtension(name: string): string {
  const dot: number = name.lastIndexOf('.');
  return dot > 0 ? name.substring(dot + 1).toLowerCase() : '';
}

function byName(a: { name: string }, b: { name: string }): number {
  return a.name.localeCompare(b.name);
}

/**
 * Turns the flat delta item list into a normalized tree with paths, depths and
 * per-folder totals. Pure, so it can be tested without Graph.
 */
export function buildScanResult(
  library: ILibraryInfo,
  items: IDriveItem[],
  startedAt: number,
  finishedAt: number,
  protection: IFolderProtection = { folders: {} }
): IScanResult {
  const live: IDriveItem[] = items.filter(i => !i.deleted);
  const rootItem: IDriveItem | undefined = live.filter(i => !!i.root)[0];
  if (!rootItem) {
    throw new Error('Scan did not return the library root folder.');
  }

  const folders: { [id: string]: IFolderItem } = {};
  const files: { [id: string]: IFileItem } = {};

  for (const item of live) {
    if (item.folder || item.root) {
      folders[item.id] = {
        id: item.id,
        name: item.root ? library.name : item.name,
        parentId: item.root ? undefined : item.parentReference?.id,
        path: '',
        depth: 0,
        lastModifiedDateTime: item.lastModifiedDateTime ?? '',
        webUrl: item.webUrl ?? '',
        childFolderIds: [],
        fileIds: [],
        totalFileCount: 0,
        totalSize: 0
      };
    }
  }

  const fileItems: IDriveItem[] = [];
  for (const item of live) {
    if (item.file) {
      fileItems.push(item);
    }
  }

  // Link children to parents. Anything whose parent was not returned is dropped
  // rather than guessed at; delta always returns parents for a full enumeration.
  const folderList: IFolderItem[] = Object.keys(folders).map(id => folders[id]).sort(byName);
  for (const folder of folderList) {
    const parent: IFolderItem | undefined = folder.parentId ? folders[folder.parentId] : undefined;
    if (parent) {
      parent.childFolderIds.push(folder.id);
    }
  }

  // Set aside the organizer's own folders before building paths.
  const root: IFolderItem = folders[rootItem.id];
  const intakeId: string | undefined = root.childFolderIds.filter(id => folders[id].name.toLowerCase() === INTAKE_FOLDER_NAME.toLowerCase())[0];
  root.childFolderIds = root.childFolderIds.filter(id => id !== intakeId);
  // Kept aside now: unreachable folders (which _Intake becomes) are removed further down.
  const intakeSource: IFolderItem | undefined = intakeId ? folders[intakeId] : undefined;
  const managedIds: { [id: string]: boolean } = {};
  const managedQueue: string[] = root.childFolderIds.filter(id => MANAGED_FOLDER_NAMES.indexOf(folders[id].name.toLowerCase()) !== -1);
  root.childFolderIds = root.childFolderIds.filter(id => managedQueue.indexOf(id) === -1);
  while (managedQueue.length > 0) {
    const id: string = managedQueue.shift()!;
    managedIds[id] = true;
    managedQueue.push(...folders[id].childFolderIds);
  }
  const managedFileCount: number = fileItems.filter(i => !!i.parentReference?.id && managedIds[i.parentReference.id]).length;

  // Assign paths and depths top-down (breadth-first from the root).
  const queue: IFolderItem[] = [root];
  const reachable: IFolderItem[] = [];
  while (queue.length > 0) {
    const folder: IFolderItem = queue.shift()!;
    reachable.push(folder);
    for (const childId of folder.childFolderIds) {
      const child: IFolderItem = folders[childId];
      child.depth = folder.depth + 1;
      child.path = folder.path ? `${folder.path}/${child.name}` : child.name;
      queue.push(child);
    }
  }
  const reachableIds: { [id: string]: boolean } = {};
  for (const folder of reachable) {
    reachableIds[folder.id] = true;
  }
  for (const id of Object.keys(folders)) {
    if (!reachableIds[id]) {
      delete folders[id];
    }
  }

  const toFile = (item: IDriveItem, parent: IFolderItem): IFileItem => ({
    id: item.id,
    name: item.name,
    extension: getExtension(item.name),
    size: item.size ?? 0,
    createdDateTime: item.createdDateTime ?? '',
    lastModifiedDateTime: item.lastModifiedDateTime ?? '',
    createdBy: item.createdBy?.user?.displayName ?? '',
    modifiedBy: item.lastModifiedBy?.user?.displayName ?? '',
    parentId: parent.id,
    parentPath: parent.path,
    webUrl: item.webUrl ?? '',
    mimeType: item.file?.mimeType ?? '',
    quickXorHash: item.file?.hashes?.quickXorHash
  });

  // Files waiting in _Intake (top level only; subfolders there are left alone).
  let intake: IScanResult['intake'];
  if (intakeId && intakeSource) {
    const intakeFolder: IFolderItem = { ...intakeSource, path: intakeSource.name, depth: 1, childFolderIds: [], fileIds: [] };
    const intakeFiles: IFileItem[] = fileItems
      .filter(i => i.parentReference?.id === intakeId)
      .sort(byName)
      .map(i => toFile(i, intakeFolder));
    intakeFolder.fileIds = intakeFiles.map(f => f.id);
    intakeFolder.totalFileCount = intakeFiles.length;
    intake = { folder: intakeFolder, files: intakeFiles };
  }

  let totalSize: number = 0;
  fileItems.sort(byName);
  for (const item of fileItems) {
    const parent: IFolderItem | undefined = item.parentReference?.id ? folders[item.parentReference.id] : undefined;
    if (!parent) {
      continue;
    }
    const size: number = item.size ?? 0;
    files[item.id] = toFile(item, parent);
    parent.fileIds.push(item.id);
    parent.totalFileCount++;
    parent.totalSize += size;
    totalSize += size;
  }

  // Roll totals up bottom-up: deepest folders first.
  let maxDepth: number = 0;
  const deepestFirst: IFolderItem[] = reachable.slice().sort((a, b) => b.depth - a.depth);
  for (const folder of deepestFirst) {
    maxDepth = Math.max(maxDepth, folder.depth);
    const parent: IFolderItem | undefined = folder.parentId ? folders[folder.parentId] : undefined;
    if (parent) {
      parent.totalFileCount += folder.totalFileCount;
      parent.totalSize += folder.totalSize;
    }
  }

  const protectedFolders: { [folderId: string]: string } = {};
  for (const id of Object.keys(protection.folders)) {
    if (folders[id]) {
      protectedFolders[id] = protection.folders[id];
    }
  }
  if (protection.protectAllTopLevel) {
    for (const id of folders[rootItem.id].childFolderIds) {
      protectedFolders[id] = protectedFolders[id] || protection.protectAllTopLevel;
    }
  }

  return {
    library,
    rootId: rootItem.id,
    protectedFolders,
    intake,
    folders,
    files,
    fileCount: Object.keys(files).length,
    folderCount: reachable.length - 1,
    totalSize,
    maxDepth,
    managedItemCount: managedFileCount,
    scannedAt: new Date(finishedAt).toISOString(),
    durationMs: finishedAt - startedAt
  };
}

/** Scans one document library: reads structure and metadata only, never file content. */
export class LibraryScanner {
  constructor(private readonly _graph: GraphService) {}

  public async scan(
    library: ILibraryInfo,
    onProgress?: (progress: IScanProgress) => void,
    isCancelled?: () => boolean
  ): Promise<IScanResult> {
    const startedAt: number = Date.now();
    const items: IDriveItem[] = await this._graph.getAllItems(
      library.id,
      itemsRead => onProgress && onProgress({ itemsRead }),
      isCancelled
    );
    const protection: IFolderProtection = await this._graph.getProtectedFolders(library.id);
    return buildScanResult(library, items, startedAt, Date.now(), protection);
  }
}
